import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CreateStorageForm from './CreateStorageForm.tsx';
import { Button, Container, Paper, Box, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CreateStorageRequest } from '../../application/types/requests/createStorageRequest.ts';
import { Drug } from '../../application/types/drug.ts';
import { StorageLocationType } from '../../application/types/storageLocation.ts';
import StorageService from './services/StorageService.ts';
import { red } from '@mui/material/colors';
import CircularProgress from '@mui/material/CircularProgress';
import { Save } from '@mui/icons-material';
import { useGlobalStore } from '../../application/store/store.tsx';
import { NavigationRoutes } from '../../application/router/routes.ts';

const CreateStorage = () => {
    const storageService = new StorageService();
    const [showConflictMessage, setShowConflictMessage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const setMessage = useGlobalStore((state) => state.setMessage);
    const [storageData, setStorageData] = useState({
        digitalId: '',
        name: '',
        location: '',
        description: '',
        isSatelliteStorage: false,
        drugThresholds: [],
        barcode: '',
        type: StorageLocationType.StorageCabinet,
    });
    const navigate = useNavigate();

    const checkValidForm = () => {
        return storageData.name.trim() !== '';
    };

    const handleSubmit = async () => {
        if (checkValidForm()) {
            setShowConflictMessage(false);
            // OrganizationId is automatically set from JWT token on the server
            const data: CreateStorageRequest = {
                digitalId: storageData.digitalId,
                name: storageData.name,
                location: storageData.location,
                description: storageData.description,
                isSatelliteStorage: storageData.isSatelliteStorage,
                barcode: storageData.barcode,
                type: storageData.type,
                drugThresholds: storageData.drugThresholds.map((threshold) => ({
                    maxValue: parseInt(threshold.max),
                    warningThreshold: parseInt(threshold.warning),
                    alertThreshold: parseInt(threshold.alarm),
                    drug: threshold.drug as Drug,
                })),
            };

            try {
                setIsLoading(true);
                await storageService.createStorage(data).then((result) => {
                    navigate(
                        `${NavigationRoutes.storageManagement}/${NavigationRoutes.storageView}/${result}`,
                        { state: { id: result } }
                    );
                });
                setMessage(
                    'Glöm inte att lägga till Läkemedel samt Gränsvärden!',
                    'success',
                    10000
                );
            } catch (error) {
                setShowConflictMessage(true);
                console.error('Error creating storage location:', error);
            } finally {
                setIsLoading(false);
            }
        } else {
            setMessage('Fyll i Namn innan du fortsätter.', 'warning');
        }
    };

    const handleStorageChange = (e) => {
        const { name, value, type, checked } = e.target;
        setStorageData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    return (
        <Container>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('..')}
                color="primary"
            >
                Avsluta
            </Button>
            <Paper className="p-10 max-w-2xl mx-auto">
                <CreateStorageForm
                    handleChange={handleStorageChange}
                    storageData={storageData}
                />

                <Box display="flex" justifyContent="space-between" mt={3}>
                    <Button
                        startIcon={
                            isLoading ? (
                                <CircularProgress size={24} color="info" />
                            ) : (
                                <Save />
                            )
                        }
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!storageData.name}
                    >
                        Spara
                    </Button>
                </Box>
                {showConflictMessage && (
                    <Alert
                        severity="error"
                        sx={{ mt: 2, backgroundColor: red[100] }}
                    >
                        Det finns redan ett lagerställe med detta namn. Namn och
                        digitalt ID måste vara unikt.
                    </Alert>
                )}
            </Paper>
        </Container>
    );
};

export default CreateStorage;
